import React from 'react'
import { Button, Icon, Message } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import SuperCutoffProgressBar from '../SuperCutoffProgressBar'
import '../SuperCutoff.css'

// Fonte di verità unica per gli stati validi — usata sia in statusKnown che in PropTypes
const KNOWN_STATUSES = ['in_progress', 'completed', 'completed_with_errors']

const DESCRIPTION_PART1 = 'Monitor the closure queue progress. The process typically takes 30\u201360 minutes. Use the '
const DESCRIPTION_PART2 = ' button to update the status manually.'
const WAITING_HEADER = 'Waiting for closure to start'
const WAITING_BODY =
  'Complete Step 7 to trigger the closure queue, then click Refresh to monitor progress.'
const COMPLETED_HEADER = 'Closure completed successfully'
const IN_PROGRESS_HEADER = 'Closure in progress'
const ERRORS_HEADER = 'Closure completed with errors'
const UNKNOWN_STATUS_HEADER = 'Status unavailable'
const UNKNOWN_STATUS_BODY =
  'The closure status could not be determined. Refresh to retry.'
const ARIA_LABEL_REFRESH = 'Refresh closure progress'
const LABEL_REFRESH = 'Refresh'
const LABEL_BADGE_COMPLETED = 'Step completed'
const LABEL_BADGE_ERRORS = 'Completed with errors'

/**
 * Costruisce il testo del messaggio di completamento con successo.
 * @param {number} total - Numero totale di budget processati
 * @returns {string}
 */
const buildCompletedBody = (total) =>
  `All ${total} budgets have been processed. You can proceed to the next step.`

/**
 * Costruisce il testo del messaggio di completamento con errori.
 * @param {number} processed - Budget processati con successo
 * @param {number} errors - Budget con errori
 * @returns {string}
 */
const buildErrorsBody = (processed, errors) =>
  `${processed} budgets processed, ${errors} errors. Proceed to the next step to review and retry the failed budgets.`

/**
 * Costruisce il testo del messaggio di elaborazione in corso.
 * @param {number} remaining - Budget ancora in coda
 * @returns {string}
 */
const buildInProgressBody = (remaining) =>
  `${remaining} budgets remaining in queue. Refresh to update.`

/**
 * Deriva lo stato di completamento dal dato progress.
 * @param {object|null|undefined} progress - Dati progresso ricevuti dal parent
 * @returns {{ isCompleted: boolean, isCompletedWithErrors: boolean }}
 */
const deriveCompletionState = (progress) => {
  const isCompleted =
    !!progress &&
    (progress.status === 'completed' || progress.status === 'completed_with_errors')
  const isCompletedWithErrors = !!progress && progress.status === 'completed_with_errors'
  return { isCompleted, isCompletedWithErrors }
}

/**
 * Step 8 — Monitoraggio progresso della coda di closure.
 * Mostra la progress bar con refresh manuale e messaggi di stato contestuali.
 *
 * Props:
 *   progress    {object}    [opzionale] Dati progresso.
 *                           Campi: total {number}, processed {number}, remaining {number},
 *                           errors {number}, status {'in_progress'|'completed'|'completed_with_errors'},
 *                           execution_id {string}
 *   lastUpdated {string}    [opzionale] Timestamp ultimo refresh
 *   onRefresh   {Function}  Callback per il refresh manuale. Firma: fn()
 *                           Nota: stabilizzare con useCallback nel parent per massimizzare
 *                           l'efficacia di React.memo su questo componente.
 *   loading     {boolean}   [opzionale] true durante il refresh. Default: false
 */
const StepProgress = ({ progress, lastUpdated, onRefresh, loading = false }) => {
  const { isCompleted, isCompletedWithErrors } = deriveCompletionState(progress)

  // Destrutturazione con default per proteggere SuperCutoffProgressBar (props .isRequired)
  const {
    total = 0,
    processed = 0,
    remaining = 0,
    errors = 0,
    status,
  } = progress || {}

  // statusKnown è false anche quando status è undefined (campo assente nella risposta API):
  // in quel caso il componente mostra "Status unavailable", che è il fallback corretto.
  const statusKnown = !progress || KNOWN_STATUSES.indexOf(status) !== -1

  // Guard contro onRefresh non fornita: evita eccezioni a runtime se il parent
  // viola il PropTypes passando undefined. Il PropTypes .isRequired segnala l'errore
  // in sviluppo; questa guard protegge in produzione.
  const handleRefresh = () => {
    if (typeof onRefresh === 'function') onRefresh()
  }

  return (
    <div>
      <p className="supercutoff-step-description">
        {DESCRIPTION_PART1}
        <strong>Refresh</strong>
        {DESCRIPTION_PART2}
      </p>

      {/* aria-live="polite" + aria-atomic="true": i messaggi di stato vengono annunciati
          dai lettori di schermo dopo ogni refresh senza interrompere la lettura in corso */}
      <div aria-live="polite" aria-atomic="true">
        {!progress && (
          <Message info icon>
            <Icon name="info circle" aria-hidden="true" />
            <Message.Content>
              <Message.Header>{WAITING_HEADER}</Message.Header>
              {WAITING_BODY}
            </Message.Content>
          </Message>
        )}

        {progress && status === 'completed' && (
          <Message positive icon>
            <Icon name="check circle" aria-hidden="true" />
            <Message.Content>
              <Message.Header>{COMPLETED_HEADER}</Message.Header>
              {buildCompletedBody(total)}
            </Message.Content>
          </Message>
        )}

        {progress && status === 'completed_with_errors' && (
          <Message warning icon>
            <Icon name="warning sign" aria-hidden="true" />
            <Message.Content>
              <Message.Header>{ERRORS_HEADER}</Message.Header>
              {buildErrorsBody(processed, errors)}
            </Message.Content>
          </Message>
        )}

        {progress && status === 'in_progress' && (
          <Message info icon>
            <Icon name="spinner" loading aria-hidden="true" />
            <Message.Content>
              <Message.Header>{IN_PROGRESS_HEADER}</Message.Header>
              {buildInProgressBody(remaining)}
            </Message.Content>
          </Message>
        )}

        {progress && !statusKnown && (
          <Message warning icon>
            <Icon name="warning sign" aria-hidden="true" />
            <Message.Content>
              <Message.Header>{UNKNOWN_STATUS_HEADER}</Message.Header>
              {UNKNOWN_STATUS_BODY}
            </Message.Content>
          </Message>
        )}
      </div>

      {progress && (
        <SuperCutoffProgressBar
          total={total}
          processed={processed}
          remaining={remaining}
          errors={errors}
          lastUpdated={lastUpdated}
        />
      )}

      {/* Il pulsante Refresh rimane nel tab order anche durante il loading:
          aria-busy comunica lo stato di occupato ai lettori di schermo senza
          rimuovere l'elemento dalla navigazione da tastiera.
          `disabled={loading}` aggiunge il feedback visivo (opacita' ridotta) durante
          la chiamata: senza, il bottone resta visivamente identico e l'utente non
          percepisce che il click ha avuto effetto se la GET risponde rapidamente.
          Il doppio click è prevenuto sia da Semantic UI (che blocca i click durante
          loading) sia dalla guard in handleRefresh tramite il flag loading
          gestito nel parent — se il parent non lo gestisce, aggiungere: if (loading) return */}
      {!isCompleted && (
        <div className="supercutoff-progress-refresh-wrapper">
          <Button
            size="small"
            icon
            labelPosition="left"
            onClick={handleRefresh}
            loading={loading}
            disabled={loading}
            aria-label={ARIA_LABEL_REFRESH}
            aria-busy={loading}
          >
            <Icon name="refresh" aria-hidden="true" />
            {LABEL_REFRESH}
          </Button>
        </div>
      )}

      <div className="supercutoff-step-actions">
        {/* role="status" rende il badge una live region implicita:
            viene annunciato dai lettori di schermo quando appare nel DOM */}
        {isCompleted && (
          <span className="supercutoff-status-badge completed" role="status">
            <Icon name="check" aria-hidden="true" />
            {isCompletedWithErrors ? LABEL_BADGE_ERRORS : LABEL_BADGE_COMPLETED}
          </span>
        )}
      </div>
    </div>
  )
}

StepProgress.propTypes = {
  progress: PropTypes.shape({
    total: PropTypes.number,
    processed: PropTypes.number,
    remaining: PropTypes.number,
    errors: PropTypes.number,
    status: PropTypes.oneOf(KNOWN_STATUSES),
    execution_id: PropTypes.string,
  }),
  lastUpdated: PropTypes.string,
  onRefresh: PropTypes.func.isRequired,
  loading: PropTypes.bool,
}

export default React.memo(StepProgress)
